import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LogOut, Clock, Check, X, ChefHat, Package, CheckCircle, Loader2, Users, Shield, UserCog, MessageSquare, BarChart3, Settings, Volume2, VolumeX, TrendingUp, DollarSign, ShoppingBag, Store, MapPin, Ban, Star, ImageIcon, CalendarDays, Sparkles, UtensilsCrossed, Menu, Database, ClipboardCheck, CreditCard, Banknote, Smartphone, FileText, Truck, FolderOpen } from "lucide-react";
import { RESTAURANT_LOCATIONS } from "@/lib/locations";
import ThemeToggle from "@/components/ThemeToggle";
import { STATUS_LABELS, STATUS_COLORS, TIME_RANGES, MAPS_ORIGIN_ADDRESS } from "./adminShared";
import AccountingTab from "./AccountingTab";
import AIAssistantTab from "./AIAssistantTab";
import ProposalsTab from "./ProposalsTab";
import AdminProfileTab from "./AdminProfileTab";
import SettingsPanel from "./SettingsPanel";
import VisualsPanel from "./VisualsPanel";
import PromotionsPanel from "./PromotionsPanel";
import InventoryPanel from "./InventoryPanel";
import StaffManagement from "./StaffManagement";
import MessagesPanel from "./MessagesPanel";
import DatabasePanel from "./DatabasePanel";
import MenuManagement from "./MenuManagement";
import CalendarView from "./CalendarView";
import InvoicesPanel from "./InvoicesPanel";
import CustomersPanel from "./CustomersPanel";
import BookingsPanel from "./BookingsPanel";
import InquiriesPanel from "./InquiriesPanel";
import QuotesPanel from "./QuotesPanel";
import SuppliersPanel from "./SuppliersPanel";
import DocumentsPanel from "./DocumentsPanel";

function useNotificationSound() {
  const audioRef = useRef(null);
  const [soundEnabled, setSoundEnabled] = useState(() => localStorage.getItem("biten_sound") !== "off");

  useEffect(() => {
    localStorage.setItem("biten_sound", soundEnabled ? "on" : "off");
  }, [soundEnabled]);

  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      if (!audioRef.current) {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioRef.current = ctx;
      }
      const ctx = audioRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = "sine";
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = 1000;
        osc2.type = "sine";
        gain2.gain.setValueAtTime(0.3, ctx.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc2.start(ctx.currentTime);
        osc2.stop(ctx.currentTime + 0.4);
      }, 200);
    } catch (e) { /* audio not available */ }
  }, [soundEnabled]);

  return { soundEnabled, setSoundEnabled, playSound };
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [settings, setSettings] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null); // YYYY-MM-DD or null for "today"
  const [dateOrders, setDateOrders] = useState(null); // orders for selected date
  const [loadingDateOrders, setLoadingDateOrders] = useState(false);
  const [acceptDialog, setAcceptDialog] = useState({ open: false, orderId: null });
  const [estTime, setEstTime] = useState("10-20");
  const [rejectDialog, setRejectDialog] = useState({ open: false, orderId: null });
  const [rejectReason, setRejectReason] = useState("");
  const [lowStockCount, setLowStockCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingProposals, setPendingProposals] = useState(0);
  const wsRef = useRef(null);
  const prevOrderCountRef = useRef(0);
  const interceptorRef = useRef(null);
  const { soundEnabled, setSoundEnabled, playSound } = useNotificationSound();

  const isAdmin = admin?.role === "admin";
  const userPermissions = admin?.permissions || [];
  const hasPermission = (perm) => isAdmin || userPermissions.includes(perm);
  const canSwitchToCustomer = typeof window !== "undefined"
    && ["localhost", "127.0.0.1"].includes(window.location.hostname)
    && Boolean(localStorage.getItem("biten_auth"));

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersRes, categoriesRes, productsRes, staffRes, analyticsRes, settingsRes, promoRes, inventoryRes] = await Promise.all([
        api.get("/admin/orders"),
        api.get("/admin/categories"),
        api.get("/admin/products"),
        api.get("/admin/staff"),
        api.get("/admin/analytics?days=7"),
        api.get("/admin/settings"),
        api.get("/admin/promo-codes"),
        api.get("/admin/inventory/overview"),
      ]);
      setOrders(ordersRes.data);
      prevOrderCountRef.current = ordersRes.data.filter(o => o.status === "waiting_for_confirmation").length;
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
      setStaffUsers(staffRes.data);
      setAnalytics(analyticsRes.data);
      setSettings(settingsRes.data);
      setPromoCodes(promoRes.data || []);
      setLowStockCount(inventoryRes.data?.low_stock_count || 0);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("biten_admin"); navigate("/manage/login"); }
    } finally { setLoading(false); }
  }, [navigate]);

  const loadUnreadMessages = useCallback(async () => {
    try {
      const res = await api.get("/admin/messages/unread-count");
      setUnreadMessages(res.data?.unread || 0);
    } catch (e) { /* */ }
    try {
      const res = await api.get("/admin/proposals/pending-count");
      setPendingProposals(res.data?.pending || 0);
    } catch (e) { /* */ }
  }, []);

  const loadOrders = useCallback(async () => {
    try {
      const res = await api.get("/admin/orders");
      const newCount = res.data.filter(o => o.status === "waiting_for_confirmation").length;
      if (newCount > prevOrderCountRef.current) {
        playSound();
        if (document.hidden) {
          document.title = `(${newCount}) Ny beställning! - Bästa Biten`;
        }
      }
      prevOrderCountRef.current = newCount;
      setOrders(res.data);
    } catch (e) { /* */ }
  }, [playSound]);

  useEffect(() => {
    const saved = localStorage.getItem("biten_admin");
    if (!saved) { navigate("/manage/login"); return; }
    const parsed = JSON.parse(saved);
    setAdmin(parsed.admin);
    // Use a request interceptor so the admin token cannot be overwritten
    // by AppProvider's useEffect (which runs after children effects in React)
    interceptorRef.current = api.interceptors.request.use((config) => {
      config.headers["Authorization"] = `Bearer ${parsed.token}`;
      return config;
    });
    loadData();
    loadUnreadMessages();
    const interval = setInterval(loadOrders, 5000);
    const msgInterval = setInterval(loadUnreadMessages, 60000);
    try {
      const wsUrl = BACKEND_URL.replace("https", "wss").replace("http", "ws");
      const restId = localStorage.getItem("biten_restaurant_id") || "olofstrom";
      const ws = new WebSocket(`${wsUrl}/api/ws/admin_orders?token=${encodeURIComponent(parsed.token)}&restaurant=${encodeURIComponent(restId)}`);
      ws.onmessage = () => loadOrders();
      wsRef.current = ws;
    } catch (e) { /* polling fallback */ }
    return () => {
      clearInterval(interval);
      clearInterval(msgInterval);
      wsRef.current?.close();
      if (interceptorRef.current !== null) api.interceptors.request.eject(interceptorRef.current);
    };
  }, [navigate, loadData, loadOrders, loadUnreadMessages]);

  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) document.title = "Bästa Biten - Dashboard";
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const handleAccept = async () => {
    try {
      await api.post(`/admin/orders/${acceptDialog.orderId}/accept`, { estimated_time: estTime });
      toast.success("Order accepterad");
      setAcceptDialog({ open: false, orderId: null });
      loadOrders();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : e.message || "Misslyckades");
      loadOrders();
    }
  };

  const handleReject = async () => {
    try {
      await api.post(`/admin/orders/${rejectDialog.orderId}/reject`, { reason: rejectReason });
      toast.success("Order avvisad");
      setRejectDialog({ open: false, orderId: null }); setRejectReason("");
      loadOrders();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : e.message || "Misslyckades");
      loadOrders();
    }
  };

  const handleStatusUpdate = async (orderId, status) => {
    try {
      const res = await api.put(`/admin/orders/${orderId}/status`, { status });
      toast.success("Orderstatus uppdaterad");
      
      if (status === "completed") {
        if (res.data.inventory?.low_stock_items?.length > 0) {
          const names = res.data.inventory.low_stock_items.map(i => i.name).join(", ");
          toast.error(`⚠️ Lagerlarm: ${names} är nu under min-nivå!`, { autoClose: 5000 });
        }
        api.get("/admin/inventory/overview").then(overviewRes => {
          setLowStockCount(overviewRes.data?.low_stock_count || 0);
        }).catch(() => {});
      }

      loadOrders();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : e.message || "Misslyckades");
      loadOrders();
    }
  };

  const handleToggleAccepting = async () => {
    try {
      const newVal = !settings?.accepting_orders;
      await api.put("/admin/settings", { accepting_orders: newVal });
      setSettings(s => ({ ...s, accepting_orders: newVal }));
      toast.success(newVal ? "Now accepting orders" : "Orders paused");
    } catch (e) { toast.error("Kunde inte uppdatera"); }
  };

  const handleLogout = () => {
    localStorage.removeItem("biten_admin");
    delete api.defaults.headers.common["Authorization"];
    navigate("/manage/login");
  };

  // Date filtering for orders
  const loadOrdersByDate = async (date) => {
    setLoadingDateOrders(true);
    try {
      const res = await api.get(`/admin/orders?date=${date}`);
      setDateOrders(res.data);
      setSelectedDate(date);
    } catch (e) { toast.error("Kunde inte ladda beställningar för datumet"); }
    finally { setLoadingDateOrders(false); }
  };

  const clearDateFilter = () => { setSelectedDate(null); setDateOrders(null); };

  const displayOrders = selectedDate && dateOrders ? dateOrders : orders;
  const filteredOrders = statusFilter === "all" ? displayOrders : displayOrders.filter(o => o.status === statusFilter);
  const newOrderCount = orders.filter(o => o.status === "waiting_for_confirmation").length;
  const activeOrderCount = orders.filter(o => ["accepted", "paid", "preparing", "ready"].includes(o.status)).length;

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayOrders = orders.filter(o => o.created_at?.startsWith(todayStr));
  const todayCompleted = todayOrders.filter(o => o.status === "completed");
  const todayRevenue = todayCompleted.reduce((s, o) => s + (o.total || 0), 0);

  if (!admin) return null;

  const currentRestId = localStorage.getItem("biten_restaurant_id") || "olofstrom";

  const navTriggerClass = "admin-nav-trigger w-full justify-start h-10 pl-4 pr-3 text-[13px] font-medium rounded-lg text-[#D4D4D8] hover:text-[#EDEDED] hover:bg-[#161617] border border-transparent transition-all data-[state=active]:bg-[#D4B483]/[0.08] data-[state=active]:text-[#D4B483] data-[state=active]:border-[#D4B483]/20 data-[state=active]:shadow-none";
  const navLabelClass = "px-4 pt-5 pb-2 text-[9px] uppercase tracking-[0.32em] text-[#A8ABB2] select-none";

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setMobileNavOpen(false); }}>
        {/* Mobile sidebar backdrop */}
        {mobileNavOpen && (
          <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden" onClick={() => setMobileNavOpen(false)} />
        )}

        {/* ===== Sidebar ===== */}
        <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0C0C0D] border-r border-[#D4B483]/10 flex flex-col transition-transform duration-300 ease-out ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
          {/* Brand */}
          <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#1A1A1A]">
            <div className="flex items-center gap-3">
              <img src="/assets/logo/logo.jpg" alt="Bästa Biten" className="h-10 w-10 object-cover rounded-full border border-[#D4B483]/30" />
              <div className="leading-none">
                <p className="font-playfair text-sm font-bold tracking-[0.14em] text-[#EDEDED]">BÄSTA BITEN</p>
                <p className="text-[8px] uppercase tracking-[0.34em] text-[#D4B483] mt-1.5">Adminpanel</p>
              </div>
            </div>
            <button onClick={() => setMobileNavOpen(false)} className="lg:hidden p-1.5 rounded-full text-[#D4D4D8] hover:text-[#EDEDED] hover:bg-[#1A1A1A] transition-colors" aria-label="Stäng meny">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto px-3 pb-4 no-scrollbar">
            <TabsList className="bg-transparent border-0 flex-col w-full h-auto p-0 gap-0.5 items-stretch">
              <p className={navLabelClass}>Drift</p>
              <TabsTrigger value="orders" className={navTriggerClass} data-testid="tab-orders">
                <ShoppingBag className="w-4 h-4 mr-2.5" /> Beställningar
                {newOrderCount > 0 && <span className="ml-auto bg-[#EF4444] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{newOrderCount}</span>}
              </TabsTrigger>
              {hasPermission("orders") && (
                <TabsTrigger value="messages" className={navTriggerClass} data-testid="tab-messages">
                  <MessageSquare className="w-4 h-4 mr-2.5" /> Meddelanden
                  {unreadMessages > 0 && <span className="ml-auto bg-[#EF4444] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadMessages}</span>}
                </TabsTrigger>
              )}
              {hasPermission("orders") && (
                <TabsTrigger value="inquiries" className={navTriggerClass} data-testid="tab-inquiries">
                  <MessageSquare className="w-4 h-4 mr-2.5" /> Förfrågningar
                </TabsTrigger>
              )}
              {hasPermission("orders") && (
                <TabsTrigger value="quotes" className={navTriggerClass} data-testid="tab-quotes">
                  <FileText className="w-4 h-4 mr-2.5" /> Offerter
                </TabsTrigger>
              )}
              {hasPermission("orders") && (
                <TabsTrigger value="bookings" className={navTriggerClass} data-testid="tab-bookings">
                  <CalendarDays className="w-4 h-4 mr-2.5" /> Bokningar
                </TabsTrigger>
              )}
              {hasPermission("orders") && (
                <TabsTrigger value="customers" className={navTriggerClass} data-testid="tab-customers">
                  <Users className="w-4 h-4 mr-2.5" /> Kunder
                </TabsTrigger>
              )}
              {hasPermission("analytics") && (
                <TabsTrigger value="analytics" className={navTriggerClass} data-testid="tab-analytics">
                  <BarChart3 className="w-4 h-4 mr-2.5" /> Analys
                </TabsTrigger>
              )}

              <p className={navLabelClass}>Restaurang</p>
              <TabsTrigger value="menu" className={navTriggerClass} data-testid="tab-menu">
                <UtensilsCrossed className="w-4 h-4 mr-2.5" /> Meny
              </TabsTrigger>
              <TabsTrigger value="inventory" className={navTriggerClass} data-testid="tab-inventory">
                <Package className="w-4 h-4 mr-2.5" /> Lager
                {lowStockCount > 0 && (
                  <span className="ml-auto bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{lowStockCount}</span>
                )}
              </TabsTrigger>
              {hasPermission("promotions") && (
                <TabsTrigger value="promotions" className={navTriggerClass} data-testid="tab-promotions">
                  <DollarSign className="w-4 h-4 mr-2.5" /> Kampanjer
                </TabsTrigger>
              )}

              {(hasPermission("staff") || hasPermission("settings") || hasPermission("accounting") || hasPermission("ai-advisor") || hasPermission("database") || hasPermission("inventory")) && (
                <p className={navLabelClass}>Administration</p>
              )}
              {hasPermission("staff") && (
                <TabsTrigger value="staff" className={navTriggerClass} data-testid="tab-staff">
                  <Users className="w-4 h-4 mr-2.5" /> Personal
                </TabsTrigger>
              )}
              {hasPermission("settings") && (
                <TabsTrigger value="settings" className={navTriggerClass} data-testid="tab-settings">
                  <Settings className="w-4 h-4 mr-2.5" /> Inställningar
                </TabsTrigger>
              )}
              {hasPermission("settings") && (
                <TabsTrigger value="visuals" className={navTriggerClass} data-testid="tab-visuals">
                  <ImageIcon className="w-4 h-4 mr-2.5" /> Utseende
                </TabsTrigger>
              )}
              {hasPermission("accounting") && (
                <TabsTrigger value="accounting" className={navTriggerClass} data-testid="tab-accounting">
                  <DollarSign className="w-4 h-4 mr-2.5" /> Beräkningar
                </TabsTrigger>
              )}
              {hasPermission("accounting") && (
                <TabsTrigger value="invoices" className={navTriggerClass} data-testid="tab-invoices">
                  <FileText className="w-4 h-4 mr-2.5" /> Fakturor
                </TabsTrigger>
              )}
              {hasPermission("inventory") && (
                <TabsTrigger value="suppliers" className={navTriggerClass} data-testid="tab-suppliers">
                  <Truck className="w-4 h-4 mr-2.5" /> Leverantörer
                </TabsTrigger>
              )}
              {hasPermission("accounting") && (
                <TabsTrigger value="documents" className={navTriggerClass} data-testid="tab-documents">
                  <FolderOpen className="w-4 h-4 mr-2.5" /> Dokument
                </TabsTrigger>
              )}
              {hasPermission("ai-advisor") && (
                <TabsTrigger value="ai-advisor" className={navTriggerClass} data-testid="tab-ai-advisor">
                  <Sparkles className="w-4 h-4 mr-2.5" /> AI-rådgivare
                </TabsTrigger>
              )}
              {hasPermission("ai-advisor") && (
                <TabsTrigger value="proposals" className={navTriggerClass} data-testid="tab-proposals">
                  <ClipboardCheck className="w-4 h-4 mr-2.5" /> Förslag
                  {pendingProposals > 0 && <span className="ml-auto bg-[#D4B483] text-[#0A0A0A] text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pendingProposals}</span>}
                </TabsTrigger>
              )}
              {hasPermission("database") && (
                <TabsTrigger value="database" className={navTriggerClass} data-testid="tab-database">
                  <Database className="w-4 h-4 mr-2.5" /> Databas
                </TabsTrigger>
              )}

              <p className={navLabelClass}>Konto</p>
              <TabsTrigger value="profile" className={navTriggerClass} data-testid="tab-profile">
                <UserCog className="w-4 h-4 mr-2.5" /> Profil
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Sidebar footer: user + logout */}
          <div className="px-4 py-4 border-t border-[#1A1A1A] space-y-3">
            <div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${isAdmin ? "bg-[#D4B483]/10 border-[#D4B483]/25" : "bg-[#3B82F6]/10 border-[#3B82F6]/25"}`}>
                {isAdmin ? <Shield className="w-4 h-4 text-[#D4B483]" /> : <UserCog className="w-4 h-4 text-[#3B82F6]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-[#EDEDED] truncate">{admin.username}</p>
                <p className={`text-[9px] uppercase tracking-[0.22em] mt-0.5 ${isAdmin ? "text-[#D4B483]" : "text-[#3B82F6]"}`}>{isAdmin ? "Admin" : "Personal"}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#D4D4D8] hover:text-[#EF4444] hover:bg-[#1A1A1A] h-8 w-8 p-0 shrink-0" title="Logga ut" data-testid="admin-logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
            {canSwitchToCustomer && (
              <button
                onClick={() => navigate("/")}
                className="w-full text-[10px] uppercase tracking-[0.22em] py-2 rounded-lg border border-[#27272A] text-[#D4D4D8] hover:text-[#EDEDED] hover:border-[#3F3F46] transition-colors"
              >
                Till kundsidan
              </button>
            )}
          </div>
        </aside>

        {/* ===== Content column ===== */}
        <div className="lg:pl-64 min-h-screen flex flex-col">
          {/* Topbar */}
          <header className="sticky top-0 z-30 bg-[#0A0A0A]/85 backdrop-blur-md border-b border-[#1A1A1A] px-3 sm:px-5 md:px-6 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <button onClick={() => setMobileNavOpen(true)} className="lg:hidden p-2 rounded-lg text-[#EDEDED] hover:bg-[#1A1A1A] transition-colors" aria-label="Öppna meny" data-testid="admin-burger-btn">
                  <Menu className="w-5 h-5" />
                </button>
                <select
                  value={currentRestId}
                  onChange={(e) => {
                    if (e.target.value) {
                      localStorage.setItem("biten_restaurant_id", e.target.value);
                      window.location.reload();
                    }
                  }}
                  className="bg-[#121212] border border-[#27272A] text-[#E8E8EA] text-xs rounded-lg px-2.5 py-1.5 outline-none focus:border-[#D4B483] max-w-[140px] truncate"
                >
                  {RESTAURANT_LOCATIONS.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-1.5 md:gap-2">
                {newOrderCount > 0 && (
                  <div className="flex items-center gap-1 bg-[#F59E0B]/10 text-[#F59E0B] px-2.5 py-1 rounded-full text-xs font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                    {newOrderCount} nya
                  </div>
                )}
                {activeOrderCount > 0 && (
                  <div className="hidden sm:flex items-center gap-1 bg-[#3B82F6]/10 text-[#3B82F6] px-2.5 py-1 rounded-full text-xs font-semibold">
                    {activeOrderCount} aktiva
                  </div>
                )}
                {settings && (
                  <button onClick={handleToggleAccepting}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${settings.accepting_orders ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#EF4444]"}`}>
                    {settings.accepting_orders ? <><span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" /> Öppet</> : <><Ban className="w-3.5 h-3.5" /> Pausad</>}
                  </button>
                )}
                <ThemeToggle />
                <button onClick={() => setSoundEnabled(!soundEnabled)}
                  className="p-2 rounded-lg hover:bg-[#1A1A1A] text-[#E8E8EA] hover:text-[#EDEDED] transition-colors"
                  title={soundEnabled ? "Ljud på" : "Ljud av"}>
                  {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 p-3 sm:p-5 md:p-6 w-full max-w-[1440px] mx-auto">
            {/* Today's Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 hover:border-[#D4B483]/30 transition-colors">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#D4B483]/10 flex items-center justify-center">
                    <ShoppingBag className="w-4 h-4 text-[#D4B483]" />
                  </div>
                  <span className="text-[11px] text-[#E8E8EA] uppercase tracking-wider font-semibold">Dagens order</span>
                </div>
                <p className="font-playfair text-3xl font-bold text-[#EDEDED]">{todayOrders.length}</p>
              </div>
              <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 hover:border-[#D4B483]/30 transition-colors">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <span className="text-[11px] text-[#E8E8EA] uppercase tracking-wider font-semibold">Slutförd</span>
                </div>
                <p className="font-playfair text-3xl font-bold text-[#EDEDED]">{todayCompleted.length}</p>
              </div>
              <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 hover:border-[#D4B483]/30 transition-colors">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F59E0B]/10 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-[#F59E0B]" />
                  </div>
                  <span className="text-[11px] text-[#E8E8EA] uppercase tracking-wider font-semibold">Pågår</span>
                </div>
                <p className="font-playfair text-3xl font-bold text-[#EDEDED]">{activeOrderCount}</p>
              </div>
              <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4 hover:border-[#D4B483]/30 transition-colors">
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-[#D4B483]/10 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-[#D4B483]" />
                  </div>
                  <span className="text-[11px] text-[#E8E8EA] uppercase tracking-wider font-semibold">Omsättning</span>
                </div>
                <p className="font-playfair text-3xl font-bold text-[#D4B483] price-glow">{todayRevenue.toFixed(0)} kr</p>
              </div>
            </div>

            {/* Main Content */}
            <div className="min-w-0">
              {/* ============ PROFILE TAB ============ */}
              <TabsContent value="profile">
                <AdminProfileTab />
              </TabsContent>

              {/* ============ ORDERS TAB ============ */}
              <TabsContent value="orders">
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <div className="flex items-center gap-2 bg-[#121212] border border-[#27272A] rounded-lg px-3 py-1.5">
                    <CalendarDays className="w-4 h-4 text-[#D4B483]" />
                    <input type="date" value={selectedDate || ""} onChange={e => { if (e.target.value) loadOrdersByDate(e.target.value); else clearDateFilter(); }} className="bg-transparent border-0 text-sm text-[#EDEDED] focus:outline-none [color-scheme:dark]" max={todayStr} />
                    {selectedDate && <button onClick={clearDateFilter} className="text-[#E8E8EA] hover:text-[#EDEDED]"><X className="w-3.5 h-3.5" /></button>}
                  </div>
                  {selectedDate && <span className="text-xs text-[#E8E8EA]"><span className="text-[#D4B483] font-medium">{displayOrders.length}</span> orders for {new Date(selectedDate + "T12:00:00").toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>}
                  {loadingDateOrders && <Loader2 className="w-4 h-4 text-[#D4B483] animate-spin" />}
                </div>

                {selectedDate && dateOrders && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                    <div className="bg-[#121212] rounded-xl border border-[#27272A] p-3"><span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Beställningar</span><p className="text-xl font-bold text-[#EDEDED] mt-1">{dateOrders.length}</p></div>
                    <div className="bg-[#121212] rounded-xl border border-[#27272A] p-3"><span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Slutförd</span><p className="text-xl font-bold text-[#10B981] mt-1">{dateOrders.filter(o => o.status === "completed").length}</p></div>
                    <div className="bg-[#121212] rounded-xl border border-[#27272A] p-3"><span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Omsättning</span><p className="text-xl font-bold text-[#D4B483] mt-1">{dateOrders.filter(o => o.status === "completed").reduce((s, o) => s + (o.total || 0), 0).toFixed(0)} kr</p></div>
                    <div className="bg-[#121212] rounded-xl border border-[#27272A] p-3"><span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Avbruten</span><p className="text-xl font-bold text-[#EF4444] mt-1">{dateOrders.filter(o => ["cancelled", "rejected"].includes(o.status)).length}</p></div>
                  </div>
                )}

                <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5 pb-1">
                  {["all", "waiting_for_confirmation", "accepted", "preparing", "ready", "completed"].map(s => (
                    <button key={s} onClick={() => setStatusFilter(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${statusFilter === s ? "bg-[#D4B483] text-[#0A0A0A]" : "bg-[#121212] text-[#E8E8EA] border border-[#27272A] hover:border-[#3F3F46]"}`}
                      data-testid={`filter-${s}`}>
                      {s === "all" ? "Alla" : STATUS_LABELS[s]} {s !== "all" && `(${displayOrders.filter(o => o.status === s).length})`}
                    </button>
                  ))}
                </div>

                {loading || loadingDateOrders ? <p className="text-[#E8E8EA] text-center py-12">Laddar beställningar...</p> :
                filteredOrders.length === 0 ? <p className="text-[#E8E8EA] text-center py-12">Inga beställningar</p> : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredOrders.map(order => (
                      <OrderCard key={order.id} order={order}
                        onAccept={(id) => { setAcceptDialog({ open: true, orderId: id }); setEstTime("10-20"); }}
                        onReject={(id) => setRejectDialog({ open: true, orderId: id })}
                        onStatusUpdate={handleStatusUpdate}
                        onRefresh={loadOrders} />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* ============ ANALYTICS TAB ============ */}
              {hasPermission("analytics") && (
                <TabsContent value="analytics">
                  <AnalyticsPanel analytics={analytics} onRefresh={async (days) => {
                    try { const res = await api.get(`/admin/analytics?days=${days}`); setAnalytics(res.data); } catch (e) { /**/ }
                  }} />
                  <Separator className="my-8 bg-[#27272A]" />
                  <CalendarView />
                </TabsContent>
              )}

              {/* ============ MENU TAB ============ */}
              <TabsContent value="menu">
                <MenuManagement categories={categories} products={products} onRefresh={loadData} canEdit={isAdmin} />
              </TabsContent>

              {/* ============ STAFF TAB ============ */}
              {hasPermission("staff") && (
                <TabsContent value="staff">
                  <StaffManagement users={staffUsers} currentAdminId={admin.id} onRefresh={loadData} />
                </TabsContent>
              )}

              {/* ============ SETTINGS TAB ============ */}
              {hasPermission("settings") && (
                <TabsContent value="settings">
                  <SettingsPanel settings={settings} onSave={(updated) => setSettings(updated)} />
                </TabsContent>
              )}

              {hasPermission("settings") && (
                <TabsContent value="visuals">
                  <VisualsPanel settings={settings} onSave={(updated) => setSettings(updated)} />
                </TabsContent>
              )}

              {hasPermission("promotions") && (
                <TabsContent value="promotions">
                  <PromotionsPanel promoCodes={promoCodes} categories={categories} products={products} onRefresh={loadData} />
                </TabsContent>
              )}

              <TabsContent value="inventory">
                <InventoryPanel products={products} />
              </TabsContent>

              {hasPermission("accounting") && (
                <TabsContent value="accounting">
                  <AccountingTab />
                </TabsContent>
              )}

              {/* ============ AI ADVISOR TAB ============ */}
              {hasPermission("ai-advisor") && (
                <TabsContent value="ai-advisor" forceMount className={activeTab === "ai-advisor" ? "" : "hidden"}>
                  <AIAssistantTab />
                </TabsContent>
              )}

              {hasPermission("ai-advisor") && (
                <TabsContent value="proposals">
                  <ProposalsTab isAdmin={isAdmin} onChange={setPendingProposals} />
                </TabsContent>
              )}

              {hasPermission("orders") && (
                <TabsContent value="messages">
                  <MessagesPanel onChange={loadUnreadMessages} />
                </TabsContent>
              )}

              {hasPermission("orders") && (
                <TabsContent value="inquiries">
                  <InquiriesPanel onConverted={() => setActiveTab("quotes")} />
                </TabsContent>
              )}

              {hasPermission("orders") && (
                <TabsContent value="quotes">
                  <QuotesPanel />
                </TabsContent>
              )}

              {hasPermission("orders") && (
                <TabsContent value="bookings">
                  <BookingsPanel />
                </TabsContent>
              )}

              {hasPermission("orders") && (
                <TabsContent value="customers">
                  <CustomersPanel />
                </TabsContent>
              )}

              {hasPermission("accounting") && (
                <TabsContent value="invoices">
                  <InvoicesPanel />
                </TabsContent>
              )}

              {hasPermission("inventory") && (
                <TabsContent value="suppliers">
                  <SuppliersPanel />
                </TabsContent>
              )}

              {hasPermission("accounting") && (
                <TabsContent value="documents">
                  <DocumentsPanel />
                </TabsContent>
              )}

              {hasPermission("database") && (
                <TabsContent value="database">
                  <DatabasePanel />
                </TabsContent>
              )}
            </div>
          </main>
        </div>
      </Tabs>

      {/* Accept Dialog */}
      <Dialog open={acceptDialog.open} onOpenChange={(open) => setAcceptDialog({ ...acceptDialog, open })}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-[#EDEDED] font-playfair">Acceptera order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Välj tillagningstid</Label>
              <div className="grid grid-cols-3 gap-2">
                {TIME_RANGES.map(({ label, value }) => (
                  <button key={value} onClick={() => setEstTime(value)}
                    className={`py-2.5 rounded-lg text-xs font-medium transition-all ${estTime === value ? "bg-[#D4B483] text-[#0A0A0A]" : "bg-[#1A1A1A] text-[#E8E8EA] border border-[#27272A] hover:border-[#D4B483]/50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleAccept} className="w-full bg-[#10B981] text-white hover:bg-[#059669] h-11 font-semibold" data-testid="confirm-accept-btn">
              Acceptera order
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}>
        <DialogContent className="bg-[#121212] border-[#27272A]">
          <DialogHeader><DialogTitle className="text-[#EDEDED] font-playfair">Avvisa order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Anledning (valfritt)</Label>
              <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="e.g. Slut på ingredienser..."
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" data-testid="reject-reason-input" />
            </div>
            <Button onClick={handleReject} className="w-full bg-[#EF4444] text-white hover:bg-[#DC2626] h-11 font-semibold" data-testid="confirm-reject-btn">
              Avvisa order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ ORDER CARD ============ */
const PAY_METHODS = [
  { id: "card", label: "Kort", icon: CreditCard },
  { id: "cash", label: "Kontant", icon: Banknote },
  { id: "swish", label: "Swish", icon: Smartphone },
];
const PAY_LABELS = { card: "Kort", cash: "Kontant", swish: "Swish" };

function OrderCard({ order, onAccept, onReject, onStatusUpdate, onRefresh }) {
  const [payDialog, setPayDialog] = useState(false);
  const [paying, setPaying] = useState(false);
  const isPaid = order.payment_status === "paid";
  const payable = !isPaid && !["waiting_for_confirmation", "rejected", "cancelled"].includes(order.status);

  const registerPayment = async (method) => {
    setPaying(true);
    try {
      await api.post(`/admin/orders/${order.id}/payment`, { method });
      toast.success(`Betalning registrerad (${PAY_LABELS[method]})`);
      setPayDialog(false);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte registrera betalningen"); }
    finally { setPaying(false); }
  };

  const undoPayment = async () => {
    if (!window.confirm("Ta bort den registrerade betalningen? Ordern blir obetald igen.")) return;
    try {
      await api.delete(`/admin/orders/${order.id}/payment`);
      toast.success("Betalningen togs bort");
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte ta bort"); }
  };

  const deliveryAddress = order.delivery_address || {};
  const destinationAddress = [
    deliveryAddress.street,
    deliveryAddress.zip_code || deliveryAddress.postal_code,
    deliveryAddress.city,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");

  const canShowLocation = order.order_type === "delivery" && Boolean(destinationAddress);

  const handleShowLocation = () => {
    if (!canShowLocation) return;

    const mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(MAPS_ORIGIN_ADDRESS)}&destination=${encodeURIComponent(destinationAddress)}&travelmode=driving`;
    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={`bg-[#121212] rounded-xl border transition-all flex flex-col ${order.status === "waiting_for_confirmation" ? "border-[#F59E0B]/50 shadow-[0_0_12px_rgba(245,158,11,0.08)]" : "border-[#27272A] hover:border-[#3F3F46]"}`}
      data-testid={`admin-order-${order.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-bold text-[#EDEDED]">{order.order_number}</span>
          <span className="text-xs text-[#E8E8EA]">
            {new Date(order.created_at).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
          </span>
        </div>
        <Badge className={`${STATUS_COLORS[order.status]} border-0 text-xs font-semibold`}>{STATUS_LABELS[order.status]}</Badge>
      </div>

      {/* Customer Info */}
      <div className="mx-4 px-3 py-2.5 rounded-lg bg-[#0A0A0A] mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-[#EDEDED]">{order.customer_name}</span>
          <span className="text-xs text-[#E8E8EA]">{order.customer_phone}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={`text-xs border-0 ${order.order_type === "delivery" ? "bg-[#3B82F6]/15 text-[#3B82F6]" : "bg-[#10B981]/15 text-[#10B981]"}`}>
            {order.order_type === "delivery" ? "Leverans" : "Avhämtning"}
          </Badge>
          {order.order_type === "delivery" && order.delivery_address?.street && (
            <span className="text-xs text-[#E8E8EA] truncate">{order.delivery_address.street}</span>
          )}
        </div>
      </div>

      {/* Items */}
      <div className="px-4 space-y-1.5 mb-3">
        {order.items.map((item, i) => (
          <div key={i} className="flex justify-between text-xs">
            <div className="flex-1 min-w-0">
              <span className="text-[#EDEDED]">{item.quantity}x {item.product_name}</span>
              {item.selected_options?.length > 0 && (
                <span className="text-[#D4D4D8] ml-1">[{item.selected_options.map(o => o.name).join(", ")}]</span>
              )}
              {item.modifiers?.length > 0 && (
                <span className="text-[#D4B483] ml-1">({item.modifiers.map(m => m.name).join(", ")})</span>
              )}
              {item.removed_ingredients?.length > 0 && (
                <span className="text-[#EF4444] ml-1">- {item.removed_ingredients.join(", ")}</span>
              )}
            </div>
            <span className="text-[#E8E8EA] ml-2 whitespace-nowrap">{item.item_total?.toFixed(0)} kr</span>
          </div>
        ))}
      </div>

      {/* Special Instructions */}
      {order.special_instructions && (
        <div className="mx-4 p-3 rounded-lg bg-[#D4B483]/5 border border-[#D4B483]/20 mb-3">
          <div className="flex items-center gap-1.5 mb-1">
            <MessageSquare className="w-3 h-3 text-[#D4B483]" />
            <span className="text-[11px] text-[#D4B483] font-semibold uppercase tracking-wider">Notering</span>
          </div>
          <p className="text-xs text-[#EDEDED] leading-relaxed">{order.special_instructions}</p>
        </div>
      )}

      {/* Review */}
      {order.review && (
        <div className="mx-4 p-3 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20 mb-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Star className="w-3 h-3 text-[#F59E0B]" />
            <span className="text-[11px] text-[#F59E0B] font-semibold uppercase tracking-wider">Recension</span>
          </div>
          <div className="flex items-center gap-0.5 mb-1">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} className={`w-3.5 h-3.5 ${s <= order.review.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#27272A]"}`} />
            ))}
            <span className="text-xs text-[#E8E8EA] ml-1.5">{order.review.rating}/5</span>
          </div>
          {order.review.comment && (
            <p className="text-xs text-[#EDEDED] leading-relaxed">{order.review.comment}</p>
          )}
        </div>
      )}

      {/* Total & Time */}
      <div className="mx-4 flex items-center justify-between pt-3 pb-0 border-t border-[#1A1A1A]">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] text-[#E8E8EA] uppercase tracking-wider font-semibold">Totalt</span>
          <span className="text-[#D4B483] font-bold text-lg">{order.total.toFixed(0)} kr</span>
          {isPaid ? (
            <button onClick={undoPayment} title="Klicka för att ångra betalningen"
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#10B981]/15 text-[#10B981] hover:bg-[#10B981]/25">
              ✓ Betald · {PAY_LABELS[order.payment_method] || order.payment_method}
            </button>
          ) : !["rejected", "cancelled"].includes(order.status) && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#F59E0B]">Obetald</span>
          )}
        </div>
        {order.estimated_time && order.status !== "completed" && (
          <span className="text-xs text-[#E8E8EA] flex items-center gap-1 bg-[#1A1A1A] px-2 py-1 rounded-md"><Clock className="w-3 h-3" />{order.estimated_time} min</span>
        )}
      </div>

      {/* Completed by */}
      {order.status === "completed" && (() => {
        const completedEntry = (order.status_history || []).slice().reverse().find(h => h.status === "completed");
        return completedEntry ? (
          <div className="mx-4 mt-3 mb-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#6B7280]/10 border border-[#6B7280]/20">
            <UserCog className="w-3 h-3 text-[#A8ACB4] flex-shrink-0" />
            <span className="text-xs text-[#A8ACB4]">
              Slutförd av <span className="text-[#E8E8EA] font-semibold">{completedEntry.changed_by}</span>
              {" · "}{new Date(completedEntry.timestamp).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        ) : null;
      })()}

      {/* Actions */}
      <div className="flex gap-2 px-4 pb-4 mt-3">
        {order.status === "waiting_for_confirmation" && (
          <>
            <Button size="sm" className="flex-1 bg-[#10B981] text-white hover:bg-[#059669] h-9 text-xs font-semibold"
              onClick={() => onAccept(order.id)} data-testid={`accept-btn-${order.id}`}>
              <Check className="w-3.5 h-3.5 mr-1.5" /> Acceptera
            </Button>
            <Button size="sm" variant="outline" className="flex-1 border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 h-9 text-xs font-semibold"
              onClick={() => onReject(order.id)} data-testid={`reject-btn-${order.id}`}>
              <X className="w-3.5 h-3.5 mr-1.5" /> Avvisa
            </Button>
          </>
        )}
        {(order.status === "accepted" || order.status === "paid") && (
          <Button size="sm" className="flex-1 bg-[#A855F7] text-white hover:bg-[#9333EA] h-9 text-xs font-semibold"
            onClick={() => onStatusUpdate(order.id, "preparing")} data-testid={`preparing-btn-${order.id}`}>
            <ChefHat className="w-3.5 h-3.5 mr-1.5" /> Börja tillaga
          </Button>
        )}
        {order.status === "preparing" && (
          <Button size="sm" className="flex-1 bg-[#22C55E] text-white hover:bg-[#16A34A] h-9 text-xs font-semibold"
            onClick={() => onStatusUpdate(order.id, "ready")} data-testid={`ready-btn-${order.id}`}>
            <Package className="w-3.5 h-3.5 mr-1.5" /> Markera klar
          </Button>
        )}
        {order.status === "ready" && (
          <Button size="sm" className="flex-1 bg-[#6B7280] text-white hover:bg-[#4B5563] h-9 text-xs font-semibold"
            onClick={() => onStatusUpdate(order.id, "completed")} data-testid={`complete-btn-${order.id}`}>
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Slutför
          </Button>
        )}
        {payable && (
          <Button size="sm" variant="outline"
            className="flex-1 border-[#D4B483]/40 text-[#D4B483] hover:bg-[#D4B483]/10 h-9 text-xs font-semibold"
            onClick={() => setPayDialog(true)} data-testid={`pay-btn-${order.id}`}>
            <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Ta betalt
          </Button>
        )}
      </div>

      {/* Register on-site payment */}
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent className="bg-[#121212] border-[#27272A] max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#EDEDED]">Ta betalt — {order.total.toFixed(0)} kr</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#C0C0C6]">Order {order.order_number} · {order.customer_name}. Hur betalade kunden på plats?</p>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {PAY_METHODS.map(m => (
              <button key={m.id} disabled={paying} onClick={() => registerPayment(m.id)}
                className="flex flex-col items-center gap-2 py-4 rounded-xl border border-[#27272A] bg-[#0A0A0A] hover:border-[#D4B483]/50 hover:bg-[#D4B483]/5 transition-all disabled:opacity-50">
                <m.icon className="w-5 h-5 text-[#D4B483]" />
                <span className="text-xs text-[#EDEDED] font-medium">{m.label}</span>
              </button>
            ))}
          </div>
          {paying && <div className="flex justify-center mt-2"><Loader2 className="w-4 h-4 text-[#D4B483] animate-spin" /></div>}
        </DialogContent>
      </Dialog>

      {canShowLocation && (
        <div className="px-4 pb-4">
          <Button size="sm" variant="outline" className="w-full border-[#3B82F6]/40 text-[#93C5FD] hover:bg-[#3B82F6]/10 h-9 text-xs font-semibold"
            onClick={handleShowLocation} data-testid={`show-location-btn-${order.id}`}>
            <MapPin className="w-3.5 h-3.5 mr-1.5" /> Visa plats
          </Button>
        </div>
      )}
    </div>
  );
}

/* ============ ANALYTICS PANEL ============ */
function AnalyticsPanel({ analytics, onRefresh }) {
  const [period, setPeriod] = useState(7);

  if (!analytics) return <p className="text-[#E8E8EA] text-center py-12">Laddar analys...</p>;

  const maxRevenue = Math.max(...analytics.revenue_by_day.map(d => d.revenue), 1);
  const maxOrders = Math.max(...analytics.orders_by_day.map(d => d.orders), 1);

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-playfair text-xl font-bold text-[#EDEDED]">Analys</h2>
        <div className="flex gap-2">
          {[7, 14, 30].map(d => (
            <button key={d} onClick={() => { setPeriod(d); onRefresh(d); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${period === d ? "bg-[#D4B483] text-[#0A0A0A]" : "bg-[#121212] text-[#E8E8EA] border border-[#27272A]"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
          <span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Omsättning</span>
          <p className="text-xl font-bold text-[#D4B483] mt-1">{analytics.total_revenue.toLocaleString()} kr</p>
        </div>
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
          <span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Beställningar</span>
          <p className="text-xl font-bold text-[#EDEDED] mt-1">{analytics.total_orders}</p>
        </div>
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
          <span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Snittorder</span>
          <p className="text-xl font-bold text-[#EDEDED] mt-1">{analytics.avg_order_value} kr</p>
        </div>
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-4">
          <span className="text-xs text-[#E8E8EA] uppercase tracking-wider font-semibold">Avbruten</span>
          <p className="text-xl font-bold text-[#EF4444] mt-1">{analytics.cancelled_orders}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Revenue Chart */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <h3 className="text-sm font-semibold text-[#EDEDED] mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#D4B483]" /> Omsättning per dag
          </h3>
          <div className="space-y-2">
            {analytics.revenue_by_day.map(d => (
              <div key={d.date} className="flex items-center gap-3">
                <span className="text-xs text-[#E8E8EA] w-12 shrink-0">{d.date.slice(5)}</span>
                <div className="flex-1 h-6 bg-[#0A0A0A] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#D4B483] to-[#E5CFA5] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max((d.revenue / maxRevenue) * 100, d.revenue > 0 ? 15 : 0)}%` }}>
                    {d.revenue > 0 && <span className="text-[9px] font-bold text-[#0A0A0A]">{d.revenue.toFixed(0)}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Orders Chart */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <h3 className="text-sm font-semibold text-[#EDEDED] mb-4 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-[#3B82F6]" /> Beställningar per dag
          </h3>
          <div className="space-y-2">
            {analytics.orders_by_day.map(d => (
              <div key={d.date} className="flex items-center gap-3">
                <span className="text-xs text-[#E8E8EA] w-12 shrink-0">{d.date.slice(5)}</span>
                <div className="flex-1 h-6 bg-[#0A0A0A] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max((d.orders / maxOrders) * 100, d.orders > 0 ? 15 : 0)}%` }}>
                    {d.orders > 0 && <span className="text-[9px] font-bold text-white">{d.orders}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Items */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <h3 className="text-sm font-semibold text-[#EDEDED] mb-4">Populära rätter</h3>
          {analytics.popular_items.length === 0 ? (
            <p className="text-[#E8E8EA] text-xs">Ingen data än</p>
          ) : (
            <div className="space-y-2.5">
              {analytics.popular_items.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#E8E8EA] w-5">{i + 1}.</span>
                    <span className="text-sm text-[#EDEDED]">{item.name}</span>
                  </div>
                  <Badge className="bg-[#D4B483]/15 text-[#D4B483] border-0 text-xs">{item.count}x</Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Order Type Split */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <h3 className="text-sm font-semibold text-[#EDEDED] mb-4">Ordertyp</h3>
          <div className="flex gap-4 items-center justify-center py-4">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#10B981]/15 flex items-center justify-center mb-2">
                <Store className="w-8 h-8 text-[#10B981]" />
              </div>
              <p className="text-lg font-bold text-[#EDEDED]">{analytics.order_type_split.pickup}</p>
              <p className="text-xs text-[#E8E8EA]">Avhämtning</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#3B82F6]/15 flex items-center justify-center mb-2">
                <MapPin className="w-8 h-8 text-[#3B82F6]" />
              </div>
              <p className="text-lg font-bold text-[#EDEDED]">{analytics.order_type_split.delivery}</p>
              <p className="text-xs text-[#E8E8EA]">Leverans</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
